import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaChartPie } from 'react-icons/fa';
import { getCurrencyDisplayName } from '../utils/currencyHelper';
import { useTheme } from '../context/ThemeContext';

ChartJS.register(ArcElement, Tooltip, Legend);

// Yardımcı fonksiyonlar
const getTypeName = (type) => {
    return getCurrencyDisplayName(type);
};

const getCategory = (type) => {
    // Yeni kodlarda altınlar XAU ile başlar (XAUTRYG, XAU_QTR vb.)
    // Veya eski kodlardan kalan 'altin' kelimesi varsa
    if (type.includes('altin') || type.startsWith('XAU')) return 'gold';
    return 'currency';
};

const PortfolioChart = ({ portfolio }) => {
    const { darkMode } = useTheme();
    if (!portfolio || portfolio.length === 0) {
        return null;
    }

    // Varlıkları tipe göre grupla ve toplam değerleri hesapla
    const groupedData = portfolio.reduce((acc, item) => {
        const type = item.type;
        if (!acc[type]) {
            acc[type] = {
                name: getTypeName(type),
                value: 0,
                category: getCategory(type)
            };
        }
        acc[type].value += item.currentValue || 0;
        return acc;
    }, {});

    const labels = Object.values(groupedData).map(item => item.name);
    const values = Object.values(groupedData).map(item => item.value);
    const total = values.reduce((a, b) => a + b, 0);

    // Renk paleti
    const colors = [
        'rgba(59, 130, 246, 0.8)',   // Mavi
        'rgba(234, 179, 8, 0.8)',    // Altın
        'rgba(34, 197, 94, 0.8)',    // Yeşil
        'rgba(168, 85, 247, 0.8)',   // Mor
        'rgba(239, 68, 68, 0.8)',    // Kırmızı
        'rgba(20, 184, 166, 0.8)',   // Teal
        'rgba(249, 115, 22, 0.8)',   // Turuncu
    ];

    const borderColors = colors.map(c => c.replace('0.8', '1'));

    const data = {
        labels,
        datasets: [
            {
                data: values,
                backgroundColor: colors.slice(0, labels.length),
                borderColor: borderColors.slice(0, labels.length),
                borderWidth: 2,
                hoverOffset: 8
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    padding: 20,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    font: {
                        size: 13
                    },
                    color: darkMode ? '#e5e7eb' : '#374151'
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.raw;
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `₺${value.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} (${percentage}%)`;
                    }
                }
            }
        },
        cutout: '60%'
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-300 ease-in-out">
            <div className="flex items-center space-x-3 mb-6">
                <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
                    <FaChartPie className="text-blue-600 dark:text-blue-400 text-xl" />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Varlık Dağılımı</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Güncel değere göre</p>
                </div>
            </div>

            <div className="h-64">
                <Doughnut data={data} options={options} />
            </div>

            {/* Toplam */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Toplam Portföy Değeri</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    ₺{total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
            </div>
        </div>
    );
};

export default PortfolioChart;
